import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactFlagsSelect from 'react-flags-select';
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { useSelector } from 'react-redux';
import { getUserLocation } from '../../utils/location';
import { Link } from 'react-router-dom';

// Register the locale
countries.registerLocale(enLocale);

// Generate a mapping of country codes to their full names
const countryNames = countries.getNames("en", { select: "official" });




const InfoTooltip = ({ text, placement = "top" }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside tap/click
  useEffect(() => {
    const closeIfOutside = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", closeIfOutside, true);
    document.addEventListener("touchstart", closeIfOutside, true);
    return () => {
      document.removeEventListener("click", closeIfOutside, true);
      document.removeEventListener("touchstart", closeIfOutside, true);
    };
  }, []);

  // Simple placement styles
  const bubbleStyle =
    placement === "top"
      ? { bottom: "100%", left: 0, transform: "translateY(0px)" }
      : { top: "100%", left: 0, transform: "translateY(0px)" };

  return (
    <span
      ref={wrapperRef}
      className="position-relative d-inline-block"
      onMouseEnter={() => setOpen(true)}   // desktop hover
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}        // keyboard
      onBlur={() => setOpen(false)}
    >
      <i
        className="fa fa-question-circle ms-1 text-primary"
        role="button"
        tabIndex={0}
        aria-label={text}
        onClick={() => setOpen((v) => !v)}  // mobile tap toggle
      />
      {open && (
        <div
          className="position-absolute bg-dark text-white small rounded px-2 py-1 shadow"
          style={{
            whiteSpace: "nowrap",
            zIndex: 1080,
            ...bubbleStyle,
          }}
          role="tooltip"
        >
          {text}
        </div>
      )}
    </span>
  );
};


const ExchangeBillboard = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    const { exchangeRates, allowedCountries, conversionRates, currencyOptions } = useSelector((state) => state.country);
    const [selected, setSelected] = useState("NG");
    const [detectedLocation, setDetectedLocation] = useState(null);
    const getCurrencyInfoByCountryCode = (code) => {
        return currencyOptions?.find(c => c.countryCode === code) || {};
    };
    const locationCountryCode = userDetails?.country_code || detectedLocation?.country_code || 'NG';
    const [selectedCountry, setSelectedCountry] = useState(locationCountryCode);
    const [sendCurrency, setSendCurrency] = useState(getCurrencyInfoByCountryCode(locationCountryCode)?.currencyCode);
    const [sendCurrencySymbol, setSendCurrencySymbol] = useState(getCurrencyInfoByCountryCode(locationCountryCode)?.symbol);
    const [isRatesLoading, setIsRatesLoading] = useState(true);

    useEffect(() => {
        const fetchLocation = async () => {
            try {
                const loc = await getUserLocation();
                setDetectedLocation(loc);
                if (!userDetails?.country_code) {
                    setSelectedCountry(loc.country_code || 'NG');
                    setSendCurrency(getCurrencyInfoByCountryCode(loc.country_code || 'NG')?.currencyCode);
                }
            } catch (error) {
                // console.error("Error fetching location:", error);
            }
        };
        fetchLocation();
    }, [userDetails?.country_code]);

    const customLabels = Object.fromEntries(
        currencyOptions
        .filter(option => allowedCountries.includes(option.countryCode.toUpperCase()))
        .map(option => {
            const countryName = countryNames[option.countryCode];
            return [
                option.countryCode,
                `${option.currencyCode}`
            ];
        })
    );

    const supportedCurrencyCountry = currencyOptions
        .filter(option => allowedCountries.includes(option.countryCode.toUpperCase()))
        .map(option => option.countryCode);

    const findRate = (from, to) => {
        if (from === to) return 1;

        const fromData = conversionRates.find(r => r.currency_code === from);
        const toData = conversionRates.find(r => r.currency_code === to);

        if (!fromData || !toData) return 0;

        const fromBuyRate = parseFloat(fromData.buy_rate);
        const toSellRate = parseFloat(toData.sell_rate);

        return parseFloat((toSellRate / fromBuyRate).toFixed(5));
    };

    const fromCurrency = sendCurrency;

    useEffect(() => {
      if (!selectedCountry) return;
    
      let derived = getCurrencyInfoByCountryCode(selectedCountry)?.currencyCode || null;
    
      if (!derived && Array.isArray(conversionRates) && conversionRates.length) {
        const match = conversionRates.find(
          r => (r.country_code || "").toUpperCase() === selectedCountry.toUpperCase()
        );
        if (match?.currency_code) derived = match.currency_code;
      }
    
      if (!derived && Array.isArray(conversionRates) && conversionRates.length) {
        derived = conversionRates[0].currency_code;
      }
    
      if (derived && derived !== sendCurrency) {
        setSendCurrency(derived);
      }

    }, [selectedCountry, currencyOptions, conversionRates]);

    useEffect(() => {
        const ready = Array.isArray(conversionRates) && conversionRates.length > 0 && !!sendCurrency;
        setIsRatesLoading(!ready);
    }, [conversionRates, sendCurrency]);

    const otherConversionRows = useMemo(() => {
        if (!conversionRates?.length || !fromCurrency) return [];
        return conversionRates
            .filter(r => r.currency_code !== fromCurrency)
            .map(r => ({
            ...r,
            exch: findRate(fromCurrency, r.currency_code),
        }));
    }, [conversionRates, fromCurrency]);


    const handleCountrySelect = (countryCode) => {
        setSelectedCountry(countryCode);
        setSendCurrency(getCurrencyInfoByCountryCode(countryCode)?.currencyCode);
        setSendCurrencySymbol(getCurrencyInfoByCountryCode(countryCode)?.symbol);
    }
    
  return (
    <div className=''>
        {/* <h6 className='mb-0 p-2 pt-3'>Exchange Rates</h6> */}
        <div className='currency__card__header'>
            <h4 className='mb-0'> Exchange Rates </h4>
            <div className="text-lg-end">
                <ReactFlagsSelect 
                    selected={selectedCountry} 
                    onSelect={handleCountrySelect} 
                    customLabels={customLabels}
                    countries={supportedCurrencyCountry}
                />
            </div>
            {/* <div className="search__container position-relative text-lg-end">
                <CiSearch className='search__currency__icon'/>
                <input type="search" name="" id="" className='search__currency__input' placeholder='Search' />
            </div> */}
        </div>
        <div className="table-responsive">
            {isRatesLoading ? (
                <div className="my-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading exchange rates...</span>
                    </div>
                </div>
            ) : (
                <table className='currency__table w-100'>
                    {/* <thead>
                        <tr>
                            <th className='small '>
                                Destination Currencies
                                <InfoTooltip
                                    text={`Exchange rate for 1 ${getCurrencyInfoByCountryCode(selectedCountry)?.currencyCode}`}
                                    placement="bottom"
                                />
                            </th>
                            <th className='small '></th>
                            <th className='small '></th>
                        </tr>
                    </thead> */}
                    <tbody>
                        {otherConversionRows.map((rate, index) => (rate.country &&
                            <tr key={index} className=''>
                                <td className="">
                                    <div className="d-flex align-items-center gap-2">
                                        <div>
                                            <img src={rate.country.iso2_code ? `https://flagcdn.com/w40/${rate.country.iso2_code.toLowerCase()}.png` : ''}
                                                alt="" width="20" height="20"
                                                className="rounded-circle shadow-sm"
                                            />
                                        </div>
                                        <div className="font-medium">{rate.currency_code}</div>
                                        <div className="text-gray-500 country__name">({rate.currency_name})</div>
                                    </div>
                                </td>
                                <td className="">{sendCurrencySymbol}{Number.isFinite(rate.exch) ? (1/rate.exch).toFixed((1/rate.exch) > 1 ? 2 : 5) : "—"}</td>
                                <td className=""><Link to='/user/converter' className='small'>Send {rate.currency_code}</Link></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    </div>
  )
}

export default ExchangeBillboard